import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";
import { WhitelistRule } from "../../../../Interfaces/interfaces";
import { apiService } from "../../../../api/api";
import {
  auditOptions,
  conditionOptions,
  fieldsOptions,
  propertyOptions,
} from "../../Objects/multiSelectObjects";

interface EditWhitelistRuleDialogProps {
  rowData: WhitelistRule | null; // Accept the row data as a prop
  visible: boolean;
  onHide: () => void;
  onSave: (rule: WhitelistRule) => void; // Callback to save the updated rule
}

const EditWhitelistRuleDialog: React.FC<EditWhitelistRuleDialogProps> = ({
  rowData,
  visible,
  onHide,
  onSave,
}) => {
  const [rule, setRule] = useState<WhitelistRule | null>(null);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (rowData) {
      setRule(rowData); // When rowData changes, update the local state
    }
  }, [rowData]);

  const handleChange = (field: string, value: string) => {
    if (rule) {
      setRule({ ...rule, [field]: value });
    }
  };

  const handleSaveWhitelistRule = async () => {
    try {
      if (!rule) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Invalid rule data",
          life: 3000,
        });
        return;
      }
      console.log("Request body:", rule);

      const response = await apiService.youtubeScorecard.updateWhitelistRule(
        rule
      );
      console.log("API response:", response);

      onSave(rule); // Pass the updated rule back to the parent
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Whitelist rule saved",
        life: 3000,
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An unknown error occurred";
      console.error("Error saving whitelist rule:", errorMessage);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 3000,
      });
    } finally {
      onHide();
    }
  };

  return (
    <Dialog
      header={`Add New Whitelist Rule`}
      visible={visible}
      onHide={onHide}
      style={{ width: "50%" }}
    >
      {rule && (
        <div className="p-fluid p-formgrid p-grid">
          <div>
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="property">Property</label>
              <Dropdown
                inputId="property"
                value={rule.property}
                options={propertyOptions}
                onChange={(e) => handleChange("property", e.target.value)}
                optionLabel="label"
                placeholder="Select Property"
              />
            </div>
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="condition">Condition</label>
              <Dropdown
                inputId="condition"
                value={rule.condition}
                options={conditionOptions}
                onChange={(e) => handleChange("condition", e.target.value)}
                optionLabel="label"
                placeholder="Select Condition"
              />
            </div>
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="value">Value</label>
              <InputText
                id="value"
                value={rule.value}
                onChange={(e) => handleChange("value", e.target.value)}
              />
            </div>
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="audit">Audit</label>
              <Dropdown
                inputId="audit"
                value={rule.audit}
                options={auditOptions}
                onChange={(e) => handleChange("audit", e.target.value)}
                optionLabel="label"
                placeholder="Select Audit"
              />
            </div>
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="fields">Fields</label>
              <MultiSelect
                inputId="fields"
                value={rule.fields}
                options={rule.audit ? fieldsOptions[rule.audit] : []} //Show options based on the chosen Audit
                onChange={(e) => handleChange("fields", e.target.value)}
                optionLabel="label"
                placeholder={rule.fields}
              />
            </div>
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="notes">Notes</label>
              <InputText
                id="notes"
                value={rule.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
              />
            </div>

            <div className="p-dialog-footer">
              <Button
                label="Save"
                icon="pi pi-check"
                onClick={handleSaveWhitelistRule}
              />
              <Button label="Cancel" icon="pi pi-times" onClick={onHide} />
            </div>
          </div>
        </div>
      )}
      <Toast ref={toast} />
    </Dialog>
  );
};

export default EditWhitelistRuleDialog;
